import { Component, ElementRef, Inject, Input, OnInit } from "@angular/core";
import { FeaturePermissionName } from "@common/ADAPT.Common.Model/embed/feature-permission-name.enum";
import { MeetingAgendaTemplate, TemplateType } from "@common/ADAPT.Common.Model/organisation/meeting-agenda-template";
import { Team } from "@common/ADAPT.Common.Model/organisation/team";
import { Autobind } from "@common/lib/autobind.decorator/autobind.decorator";
import { ORGANISATION_CONFIGURATION_PAGE, TEAM_CONFIGURATION_PAGE } from "@common/page-route-providers";
import { IAdaptRoute } from "@common/route/page-route-builder";
import { RouteService } from "@common/route/route.service";
import { AdaptCommonDialogService } from "@common/ux/adapt-common-dialog/adapt-common-dialog.service";
import { IConfirmationDialogData } from "@common/ux/adapt-common-dialog/confirmation-dialog.component/confirmation-dialog.component";
import { BaseComponent } from "@common/ux/base.component/base.component";
import { AuthorisationService } from "@org-common/lib/authorisation/authorisation.service";
import { IMeetingAgendaDetail, MeetingAgendaType, MeetingsService } from "@org-common/lib/meetings/meetings.service";
import { MeetingsUiService } from "@org-common/lib/meetings/meetings-ui.service";
import { CommonTeamsService } from "@org-common/lib/teams/common-teams.service";
import ArrayStore from "devextreme/data/array_store";
import DataSource from "devextreme/data/data_source";
import { InitializedEvent } from "devextreme/ui/list";
import { BehaviorSubject, EMPTY, from, lastValueFrom } from "rxjs";
import { filter, finalize, map, switchMap, tap } from "rxjs/operators";
import { ConfigurationAuthService } from "../../configuration-auth.service";
import { ConfigArea } from "../../configuration-types";

const EditTemplateIdKey = "editTemplateId";

interface IGroupEntry {
    key: string;
    items: IMeetingAgendaDetail[];
}

@Component({
    selector: "adapt-configure-meeting-agenda-templates",
    templateUrl: "./configure-meeting-agenda-templates.component.html",
    styleUrls: ["./configure-meeting-agenda-templates.component.scss"],
    standalone: false,
})
export class ConfigureMeetingAgendaTemplatesComponent extends BaseComponent implements OnInit {
    public readonly CanConfigureOrganisation = ConfigurationAuthService.ConfigureOrganisationOrBilling;
    @Input() public team?: Team;

    public readonly MeetingAgendaType = MeetingAgendaType;
    public loadingTemplates = true;
    private updateTemplates$ = new BehaviorSubject<number | undefined>(undefined);

    public dataSource?: DataSource;
    public canMove = true;

    constructor(
        elementRef: ElementRef,
        private meetingsService: MeetingsService,
        private meetingsUiService: MeetingsUiService,
        private commonDialogService: AdaptCommonDialogService,
        private teamsService: CommonTeamsService,
        private authService: AuthorisationService,
        private routeService: RouteService,
        @Inject(TEAM_CONFIGURATION_PAGE) private teamConfigurationPageRoute: IAdaptRoute<{ teamId: number }>,
        @Inject(ORGANISATION_CONFIGURATION_PAGE) private organisationConfigurationPageRoute: IAdaptRoute<any>,
    ) {
        super(elementRef);
    }

    public ngOnInit() {
        let editTemplateId = this.routeService.getSearchParameterIntValue(EditTemplateIdKey);
        this.loadingTemplates = true;
        this.updateTemplates$.pipe(
            switchMap((focusTemplateId) => this.updateTemplates(focusTemplateId)),
            tap(() => this.loadingTemplates = false),
            map(() => {
                // cleanup editTemplateId parameter
                if (editTemplateId) {
                    const returnValue = editTemplateId;
                    editTemplateId = undefined;
                    this.routeService.deleteSearchParameter(EditTemplateIdKey, true, true);
                    return returnValue;
                } else {
                    return 0;
                }
            }),
            switchMap((templateId) => {
                if (templateId && this.dataSource) {
                    return from(this.dataSource.store().byKey(templateId)).pipe(
                        switchMap((item) => item ? this.editAgendaTemplate(item.meetingTemplate) : EMPTY),
                        finalize(() => this.highlightTemplate(templateId)),
                    );
                } else {
                    return EMPTY;
                }
            }),
            this.takeUntilDestroyed(),
        ).subscribe();

        if (this.team) {
            this.teamsService.promiseToGetAllActiveTeams().then((teams) => {
                this.canMove = teams.filter((team) => team.teamId !== this.team!.teamId)
                    .map((team) => this.authService.currentPersonHasPermission(FeaturePermissionName.OrganisationTeamConfigure, team))
                    .includes(true);
                if (!this.canMove) {
                    // no other team to move to, perhaps moving to organisation?
                    this.canMove = this.authService.currentPersonHasPermission(FeaturePermissionName.OrganisationTeamConfigure);
                }
            });
        }
    }

    public onListInitialized(e: InitializedEvent) {
        if (e.component && !this.team) {
            const component = e.component;
            setTimeout(() => { // this is from onInitialized callback -> cannot collapse yet - need to wait for the list to be fully initialized
                const groupedItems = component.getDataSource().items();
                for (let i = 1; i < groupedItems.length; i++) {
                    component.collapseGroup(i);
                }
            });
        }
    }

    public searchTemplateFunction(template: IMeetingAgendaDetail) {
        return [template.meetingTemplate.name, template.meetingTemplate.team?.name ?? "Organisation templates"];
    }

    @Autobind
    public addNewAgendaTemplate() {
        return this.meetingsService.createAgendaTemplate(this.team?.teamId).pipe(
            switchMap((agendaTemplate) => this.meetingsUiService.editAgendaTemplate(agendaTemplate)),
            tap((agendaTemplate) => this.updateTemplates$.next(agendaTemplate.meetingAgendaTemplateId)),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public deleteAgendaTemplate(agendaTemplate: MeetingAgendaTemplate) {
        const dialogData: IConfirmationDialogData = {
            title: "Delete meeting template",
            message: `Are you sure you want to delete meeting template <b>${agendaTemplate.name}</b>?`,
            confirmButtonText: "Delete",
            cancelButtonText: "Cancel",
        };
        return this.commonDialogService.openConfirmationDialogWithBoolean(dialogData).pipe(
            filter((confirmDelete) => !!confirmDelete),
            switchMap(() => this.meetingsService.remove(agendaTemplate)),
            switchMap(() => this.meetingsService.saveEntities([agendaTemplate])),
            tap(() => this.updateTemplates$.next(undefined)),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public editAgendaTemplate(agendaTemplate: MeetingAgendaTemplate) {
        return this.meetingsUiService.editAgendaTemplate(agendaTemplate).pipe(
            tap(() => this.updateTemplates$.next(agendaTemplate.meetingAgendaTemplateId)),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public moveMeetingTemplate(agendaTemplate: MeetingAgendaTemplate) {
        return this.meetingsUiService.moveMeetingTemplate(agendaTemplate).pipe(
            tap(() => this.updateTemplates$.next(agendaTemplate.meetingAgendaTemplateId)),
            this.takeUntilDestroyed(),
        );
    }

    @Autobind
    public copyMeetingTemplate(agendaTemplate: MeetingAgendaTemplate) {
        return this.meetingsUiService.copyMeetingTemplate(agendaTemplate).pipe(
            tap((copiedTemplate) => this.updateTemplates$.next(copiedTemplate?.meetingAgendaTemplateId)),
            this.takeUntilDestroyed(),
        );
    }

    public gotoTeamConfigurationPage(groupEntry?: IGroupEntry) {
        if (!groupEntry?.items?.length || !groupEntry.items[0].meetingTemplate.teamId) {
            return;
        }

        this.teamConfigurationPageRoute.gotoRoute(
            { teamId: groupEntry.items[0].meetingTemplate.teamId },
            { section: ConfigArea[ConfigArea.Meetings] },
        ).subscribe();
    }

    public gotoOrganisationConfigurationPage() {
        this.organisationConfigurationPageRoute.gotoRoute(undefined, { section: ConfigArea[ConfigArea.Meetings] }).subscribe();
    }

    private async updateTemplates(focusTemplateId?: number) { // slightly different than template-selector, either my team or not
        let visibleTemplates: IMeetingAgendaDetail[] = [];
        let allTemplates = await lastValueFrom(this.meetingsService.getMeetingAgendaTemplates());
        allTemplates = allTemplates.filter((template) => !template.entityAspect.entityState.isAddedModifiedOrDeleted()
            && (!this.isAlto || !template.meetingDescriptionArticleSlug)); // filter out pathway templates - can't edit or delete
        if (this.team) {
            visibleTemplates = allTemplates
                .filter((template) => template.teamId === this.team!.teamId)
                .map((template) => ({
                    meetingTemplate: template,
                    type: template.templateType === TemplateType.SystemCoachOnly
                        ? MeetingAgendaType.SystemCoachOnly
                        : template.templateType === TemplateType.System
                            ? MeetingAgendaType.System
                            : MeetingAgendaType.Team,
                    key: template.meetingAgendaTemplateId,
                }));

        } else {
            visibleTemplates = allTemplates
                .map((template) => {
                    const isTeam = !!template.teamId;
                    const isSystem = !!template.code;

                    let type = MeetingAgendaType.Organisation;
                    if (isSystem) {
                        type = template.templateType === TemplateType.SystemCoachOnly
                            ? MeetingAgendaType.SystemCoachOnly
                            : MeetingAgendaType.System;
                    } else if (isTeam) {
                        type = MeetingAgendaType.Team;
                    }

                    return {
                        meetingTemplate: template,
                        type,
                        key: template.meetingAgendaTemplateId,
                    };
                });
        }

        visibleTemplates = visibleTemplates.sort(this.meetingsService.sortTemplates);
        this.dataSource = new DataSource({
            store: new ArrayStore({
                data: visibleTemplates,
                key: "key",
            }),
            group: (agenda) => agenda.meetingTemplate.team?.name ?? "000000 Organisation templates", // wants organisation templates first, then team templates
        });

        if (focusTemplateId) {
            setTimeout(() => this.highlightTemplate(focusTemplateId), 500); // wait for the list to be fully initialized
        }
    }

    private highlightTemplate(templateId: number): void {
        const id = `agenda-name-${templateId}`;
        const element = (this.elementRef?.nativeElement as HTMLElement).querySelector(`#${id}`);
        if (element?.firstElementChild) { // first child is the button - element is 0x0 - scrollIntoView doesn't work
            element.firstElementChild.scrollIntoView({ block: "center" });
            element.classList.add("highlight");
            setTimeout(() => element.classList.remove("highlight"), 2500);
        }
    }
}
